import { useMemo } from 'react';

import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';

import { EDIT_COMPONENT_TYPE } from '../../constants/common';

import AppTableDatepicker from './AppTableDatepicker';
import AppTableSelect from './AppTableSelect';

const getSortIconStyles = sortOrder => ({ color: sortOrder ? '#000' : undefined, fontSize: '16px', cursor: 'pointer' });

function renderSelect({ col, ogRender, handleChange }) {
  return (value, record) => {
    const disabled = col.editConfig?.disabledToEdit?.(record) ?? false;

    return (
      <AppTableSelect
        col={col}
        disabled={disabled}
        mode={col.editConfig?.mode}
        output={col.editConfig?.output}
        record={record}
        value={ogRender ? ogRender(value, record) : value}
        handleChange={handleChange}
      />
    );
  };
}

function renderDatepicker({ col, handleChange }) {
  return (value, record) => <AppTableDatepicker col={col} record={record} value={value} handleChange={handleChange} />;
}

const usePreparedColumns = ({
  baseColumns,
  tableColumnPreferences,
  getColumnFilterProps,
  getColumnSearchProps,
  handleChange,
  handleSave
}) => {
  return useMemo(() => {
    const columns = [];

    baseColumns?.forEach((column, i) => {
      const columnPreferences = tableColumnPreferences?.columns.find(({ key }) => key === column.configColumnKey);

      if (tableColumnPreferences?.columns && !columnPreferences) return;

      const col = {
        ...column,
        ...(column.searchable ? getColumnSearchProps(column) : {}),
        ...(column.filters ? getColumnFilterProps(column) : {}),
        title: columnPreferences?.title || column.title,
        key: column.dataIndex,
        id: i,
        order: columnPreferences?.order,
        columnReferenceId: columnPreferences?.id,
        hidden: columnPreferences ? !columnPreferences?.is_visible : undefined,
        onHeaderCell: () => ({
          id: i,
          style: {
            width: `${column.width}px`
          }
        }),
        onCell: record => {
          const style = { width: `${column.width}px` };

          if (column.noPadding === true) style.padding = 0;

          return { record, id: i, style };
        },
        sortIcon: ({ sortOrder }) =>
          sortOrder === 'ascend' ? (
            <CaretUpOutlined style={getSortIconStyles(sortOrder)} />
          ) : (
            <CaretDownOutlined style={getSortIconStyles(sortOrder)} />
          )
      };

      if (
        col.editConfig?.type === EDIT_COMPONENT_TYPE.INPUT ||
        col.editConfig?.type === EDIT_COMPONENT_TYPE.INPUT_NUMBER
      ) {
        col.onCell = record => ({
          record,
          editable: true,
          editComponentType: col.editConfig?.type,
          dataIndex: col.dataIndex,
          title: record[col.dataIndex],
          rules: col.rules,
          style: { width: `${column.width}px` },
          handleSave
        });
      }

      if (col.editConfig?.type === EDIT_COMPONENT_TYPE.SELECT) {
        const ogRender = col.render;

        col.render = renderSelect({ col, ogRender, handleChange });
      }

      if (col.editConfig?.type === EDIT_COMPONENT_TYPE.DATE_PICKER) {
        col.render = renderDatepicker({ col, handleChange });
      }

      columns.push(col);
    });

    return columns.sort((a, b) => a.order - b.order);
  }, [
    baseColumns,
    tableColumnPreferences?.columns,
    getColumnSearchProps,
    getColumnFilterProps,
    handleSave,
    handleChange
  ]);
};

export default usePreparedColumns;
